/**
 * 세션 컨텍스트 + 라우트 가드.
 *
 * 401 을 **한 곳에서** 다룬다: API 클라이언트는 `UnauthorizedError` 를 던지기만 하고(화면을
 * 옮기지 않는다 — 그러면 테스트가 브라우저 없이 불가능해진다), 여기서 그 신호를 라우팅으로
 * 번역한다. 세션 만료가 어느 화면에서 터지든 결과가 같아야 하기 때문이다(패리티 P2).
 */
/* eslint-disable react-refresh/only-export-components --
 * 이 파일은 **인증 경계 모듈**이다: Provider·훅·가드 셋이 한 계약을 이룬다. Fast Refresh 를
 * 위해 넷으로 쪼개면 401 처리가 파일 넷으로 흩어지고(패리티 P2 가 노린 "한 곳"이 사라진다),
 * 얻는 것은 개발 중 HMR 편의뿐이다. 그 값보다 경계의 응집이 크다. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ANONYMOUS, fetchSession, login as doLogin, logout as doLogout } from './session'
import type { Session } from './session'

interface SessionContextValue {
  session: Session
  loading: boolean
  login: (loginId: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(ANONYMOUS)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      setSession(await fetchSession())
    } catch {
      // 네트워크·서버 장애를 "로그아웃"으로 바꾸지 않는다 — 그러면 장애 때마다 로그인 화면이
      // 뜨고 사용자는 비밀번호를 의심한다. 상태는 유지하고 화면이 오류를 말하게 둔다.
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    /*
     * eslint react-hooks/set-state-in-effect 오탐 — 규칙이 async 경계를 넘지 못한다.
     * `refresh` 의 setState 는 전부 `await fetchSession()` **뒤**(마이크로태스크)에 일어나므로,
     * 규칙이 막으려는 "이펙트 본문에서의 동기 setState → 연쇄 렌더"가 아니다.
     * 마운트 시 세션을 한 번 묻는 것은 외부 시스템 구독에 해당한다.
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
  }, [refresh])

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      loading,
      login: async (loginId, password) => {
        setSession(await doLogin(loginId, password))
      },
      logout: async () => {
        await doLogout()
        setSession(ANONYMOUS)
      },
      refresh,
    }),
    [session, loading, refresh],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession 은 SessionProvider 안에서만 쓸 수 있습니다.')
  return value
}

/** 인증이 필요한 화면 래퍼 — 미인증이면 로그인으로, **원래 가려던 곳을 기억**해서 보낸다. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { session, loading } = useSession()
  const location = useLocation()

  if (loading) return <FullscreenNotice>{t('auth.session_checking')}</FullscreenNotice>
  if (!session.authed) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  return <>{children}</>
}

/** 관리자 전용 화면 래퍼. dev 개방이면 통과한다(백엔드 require_admin 과 대칭). */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { session, loading } = useSession()
  if (loading) return <FullscreenNotice>{t('auth.session_checking')}</FullscreenNotice>
  if (!session.authed) return <Navigate to="/login" replace />
  if (!session.isAdmin) {
    // 403 은 오류가 아니라 상태다 — 빈 화면 대신 무엇이 부족한지 말한다.
    return <FullscreenNotice>{t('auth.admin_only')}</FullscreenNotice>
  }
  return <>{children}</>
}

export function FullscreenNotice({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-8 text-sm text-ink-3">
      {children}
    </div>
  )
}
